import { Controller } from "@hotwired/stimulus";

/**
 * Highcharts for market data subpages. Uses Stimulus connect/disconnect so charts
 * initialize on Turbo visits (inline <script> tags are not reliable with Turbo Drive).
 */
export default class extends Controller {
  static targets = ["payload", "chart"];
  static values = { kind: String };

  connect() {
    this.highchartsChart = null;
    requestAnimationFrame(() => this.draw());
  }

  disconnect() {
    this.destroyChart();
  }

  destroyChart() {
    if (this.highchartsChart) {
      this.highchartsChart.destroy();
      this.highchartsChart = null;
    }
  }

  draw() {
    const Highcharts = window.Highcharts;
    if (!Highcharts || !this.hasPayloadTarget || !this.hasChartTarget) return;

    let payload;
    try {
      payload = JSON.parse(this.payloadTarget.textContent.trim());
    } catch {
      return;
    }

    this.destroyChart();
    const renderTo = this.chartTarget;

    const tenorLabels = {
      formatter: function () {
        const map = {
          0.25: "3M",
          0.5: "6M",
          1: "1Y",
          2: "2Y",
          3: "3Y",
          5: "5Y",
          7: "7Y",
          10: "10Y",
          15: "15Y",
          20: "20Y",
        };
        return map[this.value] || `${this.value}Y`;
      },
    };

    switch (this.kindValue) {
      case "yield_curve": {
        const spot = payload.spot || [];
        const spot1w = payload.spot1w || [];
        if (spot.length === 0) return;
        this.highchartsChart = Highcharts.chart(renderTo, {
          chart: { type: "spline" },
          title: { text: null },
          xAxis: {
            type: "linear",
            title: { text: "Tenor (years)" },
            min: 0,
            tickPositions: [0.25, 0.5, 1, 2, 3, 5, 7, 10, 15, 20],
            labels: tenorLabels,
          },
          yAxis: {
            title: { text: "Yield (% p.a.)" },
            labels: { format: "{value:.2f}%" },
          },
          tooltip: {
            shared: true,
            headerFormat: "<b>{point.x:.2f}Y</b><br/>",
            pointFormat:
              '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.3f}%</b><br/>',
          },
          legend: { enabled: true },
          series: [
            { name: "Today", data: spot, color: "#1e40af", lineWidth: 2 },
            {
              name: "1 Week Ago",
              data: spot1w,
              color: "#9ca3af",
              lineWidth: 1,
              dashStyle: "Dash",
            },
          ],
          credits: { enabled: false },
        });
        break;
      }
      case "spot_forward": {
        const spot = payload.spot || [];
        const forward = payload.forward || [];
        if (spot.length === 0 && forward.length === 0) return;
        this.highchartsChart = Highcharts.chart(renderTo, {
          chart: { type: "spline" },
          title: { text: null },
          xAxis: {
            type: "linear",
            title: { text: "Tenor (years)" },
            min: 0,
            tickPositions: [0.25, 0.5, 1, 2, 3, 5, 7, 10, 15, 20],
            labels: tenorLabels,
          },
          yAxis: {
            title: { text: "Rate (% p.a.)" },
            labels: { format: "{value:.2f}%" },
          },
          tooltip: {
            shared: true,
            headerFormat: "<b>{point.x:.2f}Y</b><br/>",
            pointFormat:
              '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.3f}%</b><br/>',
          },
          legend: { enabled: true },
          series: [
            { name: "Spot Yield", data: spot, color: "#1e40af", lineWidth: 2 },
            {
              name: "Forward Rate",
              data: forward,
              color: "#dc2626",
              lineWidth: 2,
              dashStyle: "ShortDash",
            },
          ],
          credits: { enabled: false },
        });
        break;
      }
      case "corporate_vs_ags": {
        const bbb5y = payload.bbb5y || [];
        const a5y = payload.a5y || [];
        const ags5y = payload.ags5y || [];
        if (bbb5y.length === 0 && a5y.length === 0) return;
        this.highchartsChart = Highcharts.chart(renderTo, {
          chart: { type: "spline" },
          title: { text: null },
          xAxis: { type: "datetime", title: { text: null } },
          yAxis: {
            title: { text: "Yield (% p.a.)" },
            labels: { format: "{value:.2f}%" },
          },
          tooltip: {
            shared: true,
            xDateFormat: "%b %Y",
            pointFormat:
              '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:.3f}%</b><br/>',
          },
          legend: { enabled: true },
          plotOptions: { arearange: { fillOpacity: 0.15 } },
          series: [
            { name: "BBB-rated 5Y", data: bbb5y, color: "#dc2626", lineWidth: 2 },
            { name: "A-rated 5Y", data: a5y, color: "#2563eb", lineWidth: 2 },
            {
              name: "AGS 5Y (Monthly)",
              data: ags5y,
              color: "#6b7280",
              lineWidth: 1,
              dashStyle: "Dash",
            },
          ],
          credits: { enabled: false },
        });
        break;
      }
      default:
        break;
    }
  }
}
